import { Injectable } from '@angular/core';
import { fromEvent, Observable, BehaviorSubject } from 'rxjs';
import { map, filter, startWith } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';

export interface ResizeEvent {
  height: number;
  width: number;
}

export enum BreakpointSizes {
  SMALL = 767,
  MEDIUM = 1024,
  LARGE = 1025
}

export enum Breakpoints {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop'
}

export type Devices = Breakpoints.MOBILE | Breakpoints.TABLET | Breakpoints.DESKTOP;

@Injectable({
  providedIn: 'root'
})
export class WindowResizeService {
  public filters$: BehaviorSubject<number> = new BehaviorSubject(0);

  public change$: Observable<ResizeEvent>;
  public device$: Observable<Devices>;
  public mobile$: Observable<boolean>;
  public tablet$: Observable<boolean>;
  public desktop$: Observable<boolean>;

  public constructor(
    private readonly router: Router
  ) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.filters$.next(0);
    });

    this.change$ = fromEvent(window, 'resize').pipe(
      map(event => event.target),
      map((event: Window) => ({
        height: event.innerHeight,
        width: event.innerWidth
      })),
      startWith({
        height: window.innerHeight,
        width: window.innerWidth
      })
    );

    this.device$ = this.change$.pipe(
      map(change => {
        switch (true) {
          case (change.width <= BreakpointSizes.SMALL):
            return Breakpoints.MOBILE;
          case (change.width <= BreakpointSizes.MEDIUM):
            return Breakpoints.TABLET;
          case (change.width >= BreakpointSizes.LARGE):
            return Breakpoints.DESKTOP;
          default:
            return Breakpoints.DESKTOP;
        }
      })
    );

    this.mobile$ = this.device$.pipe(
      map(device => device === Breakpoints.MOBILE)
    );

    this.tablet$ = this.device$.pipe(
      map(device => device === Breakpoints.TABLET)
    );

    this.desktop$ = this.device$.pipe(
      map(device => device === Breakpoints.DESKTOP)
    );
  }
}
